import { configureStore } from "@reduxjs/toolkit";
import { avatarApi } from "@store/avatarApi";
import { companionApi } from "@store/companionApi";
import { patientApi } from "@store/patientApi";
import { accountApi } from "@store/accountApi";
import { photoApi } from "@store/photoApi";
import { characterApi } from "@store/characterApi";
import { personApi } from "@store/personApi";
import { sessionApi } from "@store/sessionApi";
import { appConfigApi } from "@store/appConfigApi";
import { reminderApi } from "@store/reminderApi";
import { planApi } from "@store/planApi";
import microphoneReducer from "@store/microphoneSlice";
import photoUploadReducer from "@store/photoUploadSlice";
import companionFormReducer from "@store/companionFormSlice";
import incomingCallReducer from "@store/incomingCallSlice";
import offlineBannerReducer from "@store/offlineBannerSlice";
import subscriptionAlertReducer from "@store/subscriptionAlertSlice";
import accountSetupReducer, {
  AccountSetupState,
} from "@store/accountSetupSlice";
import signUpReducer, { SignUpState } from "@store/./signUpSlice";
import authReducer, { AuthState } from "@store/authSlice";
import resetPasswordReducer, {
  ResetPasswordState,
} from "@store/resetPasswordSlice";
import accountReducer, { AccountState } from "@store/accountSlice";
import snackbarReducer from "@store/snackbarSlice";
import inviteReducer from "@store/inviteSlice";
import homeReducer, { HomeState } from "@store/homeSlice";
import drawerReducer, { DrawerState } from "@store/sideBarSlice";
import driverReducer, { DriverState } from "@store/driverSlice";
import avatarReducer, { AvatarState } from "@store/avatarSlice";
import subscriptionReducer from "@store/subscriptionSlice";
import sessionTimedOutDialogReducer from "@store/sessionTimedOutDialogSlice";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { albumApi } from "@store/albumApi";
import { labelApi } from "@store/labelApi";
import { invitationApi } from "@store/invitationApi";
import { messageApi } from "@store/messageApi";
import { accountSetupApi } from "@store/accountSetupApi";
import { apiErrorHandler } from "@store/apiErrorHandler";
import { userApi } from "@store/userApi";
import { pushTokenApi } from '@store/pushTokenApi';
import { gameApi } from '@store/gameApi';

const getPersistConfig = (key: string) => ({
  key,
  storage,
});

export const store = configureStore({
  reducer: {
    auth: persistReducer<AuthState>(getPersistConfig("auth"), authReducer),
    signUp: persistReducer<SignUpState>(
      getPersistConfig("signUp"),
      signUpReducer,
    ),
    resetPassword: persistReducer<ResetPasswordState>(
      getPersistConfig("resetPassword"),
      resetPasswordReducer,
    ),
    drawer: persistReducer<DrawerState>(
      getPersistConfig("drawer"),
      drawerReducer,
    ),
    account: persistReducer<AccountState>(
      getPersistConfig("account"),
      accountReducer,
    ),
    accountSetup: persistReducer<AccountSetupState>(
      getPersistConfig("accountSetup"),
      accountSetupReducer,
    ),
    home: persistReducer<HomeState>(getPersistConfig("home"), homeReducer),
    subscription: subscriptionReducer,
    driver: persistReducer<DriverState>(
      getPersistConfig("driver"),
      driverReducer,
    ),
    avatar: persistReducer<AvatarState>(
      {
        ...getPersistConfig("avatar"),
        whitelist: ["subtitlesOn"],
      },
      avatarReducer,
    ),
    microphone: microphoneReducer,
    offlineBanner: offlineBannerReducer,
    photoUpload: photoUploadReducer,
    snackbar: snackbarReducer,
    invite: inviteReducer,
    companionForm: companionFormReducer,
    incomingCall: incomingCallReducer,
    sessionTimedOutDialog: sessionTimedOutDialogReducer,
    subscriptionAlert: subscriptionAlertReducer,
    avatarApi: avatarApi.reducer,
    companionApi: companionApi.reducer,
    accountApi: accountApi.reducer,
    patientApi: patientApi.reducer,
    photoApi: photoApi.reducer,
    characterApi: characterApi.reducer,
    sessionApi: sessionApi.reducer,
    personApi: personApi.reducer,
    appConfigApi: appConfigApi.reducer,
    reminderApi: reminderApi.reducer,
    albumApi: albumApi.reducer,
    labelApi: labelApi.reducer,
    invitationApi: invitationApi.reducer,
    messageApi: messageApi.reducer,
    accountSetupApi: accountSetupApi.reducer,
    userApi: userApi.reducer,
    planApi: planApi.reducer,
    pushTokenApi: pushTokenApi.reducer,
    gameApi: gameApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat([
      avatarApi.middleware,
      companionApi.middleware,
      patientApi.middleware,
      accountApi.middleware,
      photoApi.middleware,
      characterApi.middleware,
      personApi.middleware,
      sessionApi.middleware,
      appConfigApi.middleware,
      reminderApi.middleware,
      albumApi.middleware,
      labelApi.middleware,
      invitationApi.middleware,
      messageApi.middleware,
      accountSetupApi.middleware,
      userApi.middleware,
      planApi.middleware,
      pushTokenApi.middleware,
      gameApi.middleware,
      apiErrorHandler,
    ]),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
